import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { track } from '@/lib/analytics'
import { useLocale } from '@/i18n/locale'
import { TG_PERSONAL_URL, TG_CHANNEL_URL, TG_CASES_URL } from '@/lib/contacts'
import { sectionFromPath } from '@/lib/sections'
import './partners.css'

/**
 * Партнёрская программа.
 *
 * Адресат этой секции — НЕ покупатель. Это человек, у которого покупатель
 * уже есть в контактах: консультант, интегратор, коуч, бухгалтер, юрист,
 * агентство. Поэтому секция стоит ПОСЛЕ формы: кто хотел купить — уже
 * оставил заявку, а кто дочитал и не оставил, вполне может оказаться тем,
 * кто приведёт.
 *
 * Условия — со слов Тони: 10% от ВСЕГО входящего дохода по приведённому
 * клиенту, не только с первой сделки. Связь — личный Telegram. Клиент
 * закрепляется за партнёром с момента, как назван в личке.
 *
 * Срок закрепления (90 дней до зума) и порог клиента (оборот от 300 млн ₽
 * в год) подтвердил Тони 2026-09-26. Без срока «зарезервирован за вами»
 * становится бессрочным обещанием и однажды приводит к спору.
 *
 * Полоса, а не разворот (аудит 2026-09-21): блок обязателен, но занимал
 * 1,2 экрана на десктопе и 3,2 на телефоне сразу после формы — для другой
 * аудитории и с такой же яркой кнопкой, как у формы. Покупатель читал про
 * «закрепление» и «10 % с каждого платежа» про себя. Теперь на виду —
 * заголовок, суть в два предложения и вторичная кнопка; условия целиком
 * лежат в нативном <details> и остаются в SSR-разметке.
 */

const COPY = {
  ru: {
    kicker: 'Партнёрская программа',
    heading: 'Приведите клиента — получайте 10% с каждого его платежа',
    lead: 'Не разовый бонус за первую сделку: десять процентов от всего, что этот клиент платит нам, — весь срок, пока он с нами. Продажа, внедрение и сопровождение — на нас.',
    more: 'Условия полностью',
    whoLabel: 'Кого мы ждём',
    who: `Собственник компании с оборотом от 300 млн ₽ в год. У него есть команда и есть амбиция расти и наводить порядок. Он принимает решения сам. Если такой человек есть в ваших контактах — вам есть что нам передать.`,
    stepsLabel: 'Как это работает',
    steps: [
      { t: 'Пишете мне в личку и называете клиента', b: 'До того, как он к нам обратился. Одного сообщения достаточно — имя, компания, чем занимается.' },
      { t: 'Клиент закрепляется за вами', b: 'Дальше он ваш, даже если придёт к нам сам и не сошлётся на вас. Закрепление действует 90 дней до зума и продлевается на весь срок работы, если зум состоялся.' },
      { t: 'Показываете, в чём суть', b: 'Что ему ближе: этот сайт, видеокейсы или канал. Разбираться в технологии вам не нужно — нужно, чтобы он понял, о чём речь.' },
      { t: 'Назначаете зум со мной', b: 'Через ту же личку. Дальше продаём и внедряем мы, от вас больше ничего не требуется.' },
    ],
    getLabel: 'Что вы получаете',
    get: [
      '10% от каждого платежа клиента, а не только с первого',
      'Закрепление до начала переговоров — не гонка «кто первый привёл»',
      'Ноль работы после знакомства: продажа, внедрение и сопровождение на нас',
      'Прозрачность: вы видите, на какой стадии ваш клиент, и когда пришёл платёж',
    ],
    honestLabel: 'Честно о границах',
    honest: 'Мы не берём в работу всех подряд: если на бесплатной диагностике видно, что экономика внедрения у клиента не сходится, мы говорим это ему прямо. Такой клиент остаётся закреплённым за вами — вернётся, когда дорастёт.',
    cta: 'Написать в Telegram',
    ctaNote: 'Личный контакт Антона. Назовите клиента — закреплю за вами.',
    showLabel: 'Что показать клиенту',
    showCases: 'Видеокейсы',
    showChannel: 'Канал Антона',
  },
  en: {
    kicker: 'Partner programme',
    heading: 'Refer a client — earn 10% of every payment they make',
    lead: 'Not a one-off bonus on the first deal: ten percent of everything that client pays us, for as long as they stay with us. Sales, rollout and support are on us.',
    more: 'Full terms',
    whoLabel: 'Who we are looking for',
    who: `An owner of a company with revenue from $3M a year. They have a team and an ambition to grow and put things in order. They make decisions themselves. If someone like that is in your contacts, you have something to bring us.`,
    stepsLabel: 'How it works',
    steps: [
      { t: 'Message me directly and name the client', b: 'Before they reach out to us. One message is enough — name, company, what they do.' },
      { t: 'The client is reserved for you', b: 'They stay yours even if they come to us on their own without mentioning you. The reservation holds for 90 days up to the call, and extends for the whole engagement once the call happens.' },
      { t: 'Show them what it is about', b: 'Whichever fits: this site, the video cases or the channel. You do not need to understand the technology — they need to understand the point.' },
      { t: 'Set up a call with me', b: 'Through the same chat. From there we sell and deliver; nothing else is required from you.' },
    ],
    getLabel: 'What you get',
    get: [
      '10% of every payment the client makes, not just the first',
      'Reservation before negotiations start — not a race for who claimed them first',
      'Zero work after the introduction: sales, rollout and support are on us',
      'Transparency: you see what stage your client is at and when a payment lands',
    ],
    honestLabel: 'Honest about the limits',
    honest: 'We do not take everyone. If the free diagnostics shows the economics do not work at their scale, we tell them so directly. That client stays reserved for you — they will come back when they grow into it.',
    cta: 'Message me on Telegram',
    ctaNote: 'Anton’s personal contact. Name the client and I will reserve them for you.',
    showLabel: 'What to show the client',
    showCases: 'Video cases',
    showChannel: 'Anton’s channel',
  },
}

export function Partners() {
  const locale = useLocale()
  const t = COPY[locale]
  const { pathname, hash } = useLocation()
  const [open, setOpen] = useState(false)

  // Короткая ссылка /partners и пункт меню «#partners» ведут сюда за
  // условиями — раскрываем их сами, чтобы человек не искал, где читать.
  // SSR отдаёт закрытое <details>, состояние догоняет после гидратации.
  useEffect(() => {
    if (sectionFromPath(pathname) === 'partners' || hash === '#partners') setOpen(true)
  }, [pathname, hash])

  return (
    <section id="partners" className="section bg-card">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-6 lg:items-center">
          <div className="lg:col-span-8">
            <div className="font-mono text-small uppercase tracking-wider text-muted mb-3">{t.kicker}</div>
            <h2 className="text-h3 text-ink leading-tight text-balance">{t.heading}</h2>
            <p className="mt-3 text-body text-muted">{t.lead}</p>
          </div>
          {/* Вторичный стиль намеренно: главная кнопка страницы — у формы выше. */}
          <div className="lg:col-span-4 lg:justify-self-end">
            <a
              href={TG_PERSONAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary w-full sm:w-auto no-underline text-center"
              onClick={() => track('partner_tg_click')}
            >
              {t.cta}
            </a>
          </div>
        </div>

        <details
          className="partners-details mt-6 lg:mt-8"
          open={open}
          onToggle={(e) => setOpen(e.currentTarget.open)}
        >
          <summary className="inline-flex items-center gap-3 min-h-[44px] font-mono text-small font-medium text-ink hover:text-accent transition-colors rounded-btn focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent">
            <span aria-hidden className="partners-details__mark font-mono text-h3 leading-none text-muted" />
            {t.more}
          </summary>

          <div className="mt-6 pt-8 border-t border-divider grid grid-cols-1 lg:grid-cols-2 gap-x-14 gap-y-10">
            <div>
              <div className="text-small uppercase tracking-wider text-muted mb-3">{t.whoLabel}</div>
              <p className="text-small text-muted leading-relaxed">{t.who}</p>

              <div className="mt-8 text-small uppercase tracking-wider text-muted mb-3">{t.getLabel}</div>
              <ul className="space-y-2">
                {t.get.map((g) => (
                  <li key={g} className="flex gap-3 text-small text-ink leading-snug">
                    <span aria-hidden className="text-accent shrink-0">✓</span>
                    <span>{g}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 border-l-2 border-divider pl-5">
                <div className="text-small uppercase tracking-wider text-muted mb-2">{t.honestLabel}</div>
                <p className="text-small text-muted leading-relaxed">{t.honest}</p>
              </div>
            </div>

            <div>
              <div className="text-small uppercase tracking-wider text-muted mb-3">{t.stepsLabel}</div>
              <ol className="space-y-5">
                {t.steps.map((s, i) => (
                  <li key={s.t} className="flex gap-4">
                    <div className="font-mono text-small tabular-nums text-muted w-6 pt-0.5">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="flex-1 border-l border-divider pl-4">
                      <div className="text-small text-ink font-medium leading-snug">{s.t}</div>
                      <p className="mt-1 text-small text-muted leading-snug">{s.b}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-8">
                <div className="text-small uppercase tracking-wider text-muted mb-2">{t.showLabel}</div>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-small">
                  <a
                    href={TG_CASES_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent no-underline hover:underline underline-offset-4"
                    onClick={() => track('partner_cases_click')}
                  >
                    {t.showCases} →
                  </a>
                  <a
                    href={TG_CHANNEL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent no-underline hover:underline underline-offset-4"
                    onClick={() => track('partner_channel_click')}
                  >
                    {t.showChannel} →
                  </a>
                </div>
                <p className="mt-3 text-small text-muted">{t.ctaNote}</p>
              </div>
            </div>
          </div>
        </details>
      </div>
    </section>
  )
}
